// Einheitliche Produktgröße in Bildrahmen (Produktkarten, Produktgalerie).
//
// Die Packshots aus dem PIM haben unterschiedlich viel Weißrand: ein Produkt füllt 90 % seines Bildes, das nächste
// nur 50 %. Im Shop sollen alle Produkte gleich groß wirken. Dieses Modul misst je Bild den Bereich, der nicht
// weiß ist (kleine Kopie auf einem Canvas – die Shopify-CDN erlaubt das per CORS), und verschiebt/skaliert das Bild
// so, dass das Produkt mittig sitzt und eine feste Breite des Rahmens einnimmt (TARGET). Bilder ohne weißen
// Hintergrund (Szenenfotos, Infografiken) bleiben unverändert. Ergebnisse werden je Bild-URL im sessionStorage
// gemerkt.
//
// Markup: <img class="fk-pcard__image" crossorigin="anonymous" …> in einem Rahmen mit object-fit: contain.

const TARGET = 0.78; // Anteil der kürzeren Rahmenkante, den das Produkt einnehmen soll
const GALLERY_TARGET = 0.74; // Produktseite: etwas mehr Luft um das Produkt, damit es nicht wuchtig wirkt
const WHITE = 238; // ab diesem Wert (0–255) gilt ein Pixel als Hintergrund
const SAMPLE = 96; // Analysebreite in Pixeln
const SELECTOR = '.fk-pcard__image, .fk-fit, .media-fit-contain .product-media__image';
const cache = new Map();
// Layout sizes exclude our transform. Observe the image, not its transformed bounds:
// hidden menu/slider cards can become visible without a window resize.
const layoutObserver = new ResizeObserver((entries) => {
  for (const { target: img } of entries) {
    if (img.dataset.fkBox) apply(img, JSON.parse(img.dataset.fkBox));
  }
});

function storageGet(key) {
  try {
    const raw = sessionStorage.getItem('fk-fit:' + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storageSet(key, value) {
  try {
    sessionStorage.setItem('fk-fit:' + key, JSON.stringify(value));
  } catch {
    /* voller oder gesperrter Speicher – dann ohne Cache */
  }
}

function keyFor(img) {
  return (img.currentSrc || img.src).replace(/([?&])width=\d+/, '$1').slice(-120);
}

/** Misst den nicht-weißen Bereich; null, wenn der Hintergrund nicht weiß ist oder nichts messbar war. */
function analyze(img) {
  const w = SAMPLE;
  const h = Math.max(8, Math.round((SAMPLE * img.naturalHeight) / img.naturalWidth));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  try {
    ctx.drawImage(img, 0, 0, w, h);
  } catch {
    return null;
  }
  let data;
  try {
    data = ctx.getImageData(0, 0, w, h).data;
  } catch {
    return null; // Canvas "tainted" – kein CORS
  }
  const isBg = (i) => data[i + 3] < 20 || (data[i] >= WHITE && data[i + 1] >= WHITE && data[i + 2] >= WHITE);
  // Hintergrundprüfung: alle vier Ecken (3×3 Pixel) müssen weiß/transparent sein
  for (const [cx, cy] of [[0, 0], [w - 3, 0], [0, h - 3], [w - 3, h - 3]]) {
    for (let y = cy; y < cy + 3; y++) for (let x = cx; x < cx + 3; x++) if (!isBg((y * w + x) * 4)) return null;
  }
  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (isBg((y * w + x) * 4)) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0 || maxX - minX < 4 || maxY - minY < 4) return null;
  // halbes Pixel Rand, damit Kanten nicht abgeschnitten werden
  return { x: Math.max(0, minX - 0.5) / w, y: Math.max(0, minY - 0.5) / h, w: Math.min(w, maxX - minX + 2) / w, h: Math.min(h, maxY - minY + 2) / h };
}

/** Rechnet aus Rahmen, Bild und Produktbereich die Transformation aus. */
function apply(img, box) {
  const frame = { width: img.clientWidth, height: img.clientHeight };
  const nw = img.naturalWidth, nh = img.naturalHeight;
  if (!frame.width || !frame.height || !nw || !nh) return;
  const ratio = Math.min(frame.width / nw, frame.height / nh); // object-fit: contain
  const dw = nw * ratio, dh = nh * ratio;
  const dx = (frame.width - dw) / 2, dy = (frame.height - dh) / 2;
  const cx = dx + box.x * dw + (box.w * dw) / 2;
  const cy = dy + box.y * dh + (box.h * dh) / 2;
  const size = Math.max(box.w * dw, box.h * dh);
  const target = Number(img.dataset.fkTarget) || (img.classList.contains('product-media__image') ? GALLERY_TARGET : TARGET);
  let s = (target * Math.min(frame.width, frame.height)) / size;
  s = Math.min(2.6, Math.max(0.5, s));
  const tx = -(cx - frame.width / 2) * s;
  const ty = -(cy - frame.height / 2) * s;
  img.style.setProperty('--fk-fit', `translate(${tx.toFixed(1)}px, ${ty.toFixed(1)}px) scale(${s.toFixed(3)})`);
  img.style.transform = 'var(--fk-fit)';
  img.dataset.fkFit = 'done';
  // The browser must account for magnification when selecting a responsive source.
  // Otherwise a small, heavily padded packshot stays blurry after normalization.
  if (img.srcset) {
    const sizes = `${Math.ceil(frame.width * Math.max(1, s))}px`;
    if (img.sizes !== sizes) img.sizes = sizes;
  }
}

async function fit(img) {
  if (img.dataset.fkFit) return;
  img.dataset.fkFit = 'pending';
  if (!img.complete || !img.naturalWidth) {
    await new Promise((resolve) => {
      const done = () => {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        resolve();
      };
      img.addEventListener('load', done);
      img.addEventListener('error', done);
    });
  }
  if (!img.naturalWidth) { img.dataset.fkFit = 'skip'; return; }
  const key = keyFor(img);
  let box = cache.get(key) ?? storageGet(key);
  if (box === undefined || box === null) {
    box = analyze(img);
    cache.set(key, box);
    storageSet(key, box);
  }
  if (!box) { img.dataset.fkFit = 'skip'; return; }
  img.dataset.fkBox = JSON.stringify(box);
  layoutObserver.observe(img);
  apply(img, box);
}

function scan(root = document) {
  root.querySelectorAll(SELECTOR).forEach((img) => fit(img));
}

// Nach Größenänderung neu rechnen (die Transformation hängt von der Rahmengröße ab)
let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    document.querySelectorAll(SELECTOR).forEach((img) => {
      try { apply(img, JSON.parse(img.dataset.fkBox)); } catch { /* ignorieren */ }
    });
  }, 150);
});

// Neue Karten (Filter, Nachladen, Section Rendering) ebenfalls behandeln
new MutationObserver((records) => {
  for (const r of records) {
    for (const n of r.removedNodes) if (n.nodeType === 1) {
      if (n.matches?.(SELECTOR)) layoutObserver.unobserve(n);
      n.querySelectorAll(SELECTOR).forEach((img) => layoutObserver.unobserve(img));
    }
    for (const n of r.addedNodes) if (n.nodeType === 1) {
      if (n.matches?.(SELECTOR)) fit(n); else scan(n);
      // Reinserted cards may already have a measured box.
      const images = n.matches?.(SELECTOR) ? [n] : n.querySelectorAll(SELECTOR);
      for (const img of images) if (img.dataset.fkBox) layoutObserver.observe(img);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

scan();
document.addEventListener('shopify:section:load', () => scan());
